// 2026-11-03 (James's report): documents a user adds to a video: a PDF, plain text or Markdown file. Their words join
// the brief's sources (api POST /api/projects/:id/documents); the file itself isn't kept in the library.

export const DOC_MAX_BYTES = 20 * 1024 * 1024;
export const DOC_ACCEPT = "application/pdf,.pdf,text/plain,.txt,text/markdown,.md,.markdown";

type FileLike = { name: string; type: string; size: number };

/** The document type the api reads, or null when the file isn't a document. */
export function docMimeOf(f: FileLike): "application/pdf" | "text/plain" | "text/markdown" | null {
  const ext = /\.([a-z0-9]+)$/i.exec(f.name)?.[1]?.toLowerCase() ?? "";
  if (f.type === "application/pdf" || ext === "pdf") return "application/pdf";
  if (f.type === "text/markdown" || ext === "md" || ext === "markdown") return "text/markdown";
  if (f.type === "text/plain" || ext === "txt") return "text/plain";
  return null;
}
export const isDocument = (f: FileLike) => docMimeOf(f) !== null;
export const docMime = (f: FileLike) => docMimeOf(f) ?? "application/pdf";

/** Before a byte moves: too big, or there's no video to add it to. */
export function docProblem(f: FileLike, projectId: string | null): string | null {
  if (!projectId) return "Documents go on a video: open the video and add it there. Its words become the video's sources.";
  if (f.size > DOC_MAX_BYTES) return "That document is over 20 MB. Add a shorter one, or the pages that matter.";
  if (!f.size) return "That file is empty.";
  return null;
}

/** The finished row: what happened to its words. */
export function docDoneLine(r: { added: number; cut: boolean; full: boolean; pages: number | null }): string {
  if (r.full) return "The sources are full, so nothing was added. Trim them in the brief, then add it again.";
  const pages = r.pages ? ` from ${r.pages} page${r.pages === 1 ? "" : "s"}` : "";
  return `${r.added.toLocaleString()} characters${pages} added to the sources${r.cut ? " (the rest didn't fit)" : ""}`;
}

/** Screenshots pasted from the clipboard arrive as "image.png": give them a name worth keeping. */
export function pastedName(f: { name: string; type: string }, at: Date = new Date()): string {
  if (f.name && f.name !== "image.png" && f.name !== "image.jpeg" && f.name !== "image") return f.name;
  const ext = f.type === "image/jpeg" ? "jpg" : f.type === "image/webp" ? "webp" : "png";
  const p = (n: number) => String(n).padStart(2, "0");
  return `Screenshot ${at.getFullYear()}-${p(at.getMonth() + 1)}-${p(at.getDate())} ${p(at.getHours())}.${p(at.getMinutes())}.${p(at.getSeconds())}.${ext}`;
}

/** The files on a paste event (screenshots, copied files), renamed when the clipboard gave them no name. */
export function pastedFiles(dt: DataTransfer | null | undefined, at: Date = new Date()): File[] {
  if (!dt) return [];
  const out: File[] = [];
  const seen = new Set<File>();
  for (const f of Array.from(dt.files ?? [])) seen.add(f), out.push(f);
  for (const it of Array.from(dt.items ?? [])) {
    if (it.kind !== "file") continue;
    const f = it.getAsFile();
    if (f && !seen.has(f) && !out.some((x) => x.name === f.name && x.size === f.size)) out.push(f);
  }
  return out.map((f, i) => {
    const name = pastedName(f, new Date(at.getTime() + i * 1000));
    return name === f.name ? f : new File([f], name, { type: f.type, lastModified: f.lastModified });
  });
}
